import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { Button } from '@/shared/uikitV2/Button';
import { Select } from '@/shared/uikitV2/Select';

import { useModalsStore } from '../../modals.store';
import { SendInviteModalProps } from '../../types';

type FormValues = {
  profileId: number;
};

export const SendInviteModal: FC<SendInviteModalProps> = ({ beneficiary, onFinish }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'sendInvite' });
  const modals = useModalsStore();
  const { lg, sm } = useBreakpoints();
  const notices = useNotices();
  const api = useApi();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      profileId: beneficiary.centralDbProfileId,
    },
  });

  const options = [
    {
      content: { label: beneficiary.email },
      value: beneficiary.centralDbProfileId,
    },
  ];

  const onSubmit = async (data: FormValues) => {
    const res = await api.beneficiaryInvite({
      beneficiaryProfileId: data.profileId,
    });

    if (res.data) {
      notices.success(t('successMessage'));
      onFinish?.();
      modals.closeModal();
    } else {
      notices.error(t('errorMessage'));
    }
  };

  return (
    <ModalContainer
      open={true}
      centered={lg}
      footer={null}
      width={600}
      title={<Title>{t('title')}</Title>}
      onCancel={modals.closeModal}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Body>
          <Controller
            control={control}
            name="profileId"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Select
                label={t('emailPlaceholder')}
                required
                multiple={false}
                options={options}
                disabled
                status={fieldState.error ? 'error' : 'default'}
                {...field}
              />
            )}
          />
          <Text>{t('message')}</Text>
        </Body>
        <Footer className={cn(sm && 'mobile')}>
          <StyledButton onClick={modals.closeModal} type="default" htmlType="button">
            {t('cancel')}
          </StyledButton>
          <StyledButton loading={isSubmitting} className={cn('confirm')} htmlType="submit">
            {t('send')}
          </StyledButton>
        </Footer>
      </form>
    </ModalContainer>
  );
};

const ModalContainer = styled(Modal)`
  &&& {
    max-width: 400px;

    .ant-modal-header {
      margin-bottom: 0;
      font-size: 22px;
    }

    .ant-modal-content {
      padding: 16px 0;
    }

    .ant-modal-title {
      padding: 0 0 16px 24px;
      border-bottom: 1px solid ${COLORS.systemDark100};
    }
  }
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 22px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  padding: 24px;
`;

const Text = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${COLORS.systemDark200};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 16px;
  border-top: 1px solid ${COLORS.systemDark100};
  padding: 16px 24px 0 24px;

  ${media.sm} {
    flex-direction: column-reverse;
  }
`;

const StyledButton = styled(Button)`
  && {
    font-weight: bold;
    font-size: 14px;
    width: auto;
    flex-grow: 1;
  }
`;
